import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import './LoginForm.css';

/**
 * Login form component for user authentication
 * Supports normal login and advanced flows like 2FA
 */
const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // For two-factor authentication
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactorForm, setShowTwoFactorForm] = useState(false);
  
  const { 
    login, 
    error: authError, 
    clearError,
    pendingFlow,
    completeTwoFactorAuth
  } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect URL from location state or default to home
  const from = location.state?.from?.pathname || '/weaversworld';
  
  // Handler for checking if user came from email verification
  useEffect(() => {
    // Check for email verification success message
    const searchParams = new URLSearchParams(location.search);
    const verified = searchParams.get('verified');
    
    if (verified === 'true') {
      setSuccessMessage('Your email has been verified! Please log in.');
    }
  }, [location]);
  
  // Clear any auth context errors when form mounts - only run once
  const clearErrorOnMount = useCallback(() => {
    if (authError) {
      clearError();
    }
  }, [authError, clearError]);
  
  useEffect(() => {
    clearErrorOnMount();
  }, [clearErrorOnMount]);
  
  // Check for pending 2FA flow
  useEffect(() => {
    if (pendingFlow && pendingFlow.id === 'mfa_authenticate') {
      setShowTwoFactorForm(true);
    } else {
      setShowTwoFactorForm(false);
    }
  }, [pendingFlow]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormError('');
    
    // Validate form
    if (!email || !password) {
      setFormError('Please enter both email and password');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const result = await login(email, password);
      
      // Check if we need to complete a pending flow
      if (result.success) {
        // If login successful, navigate to the redirect URL
        navigate(from, { replace: true });
      } else if (result.pendingFlow && result.pendingFlow.id === 'mfa_authenticate') {
        // Show 2FA form
        setShowTwoFactorForm(true);
      } else if (result.pendingFlow && result.pendingFlow.id === 'verify_email') {
        // Need to verify email
        setFormError('Please check your email to verify your account before logging in.');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle different error scenarios
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        setFormError(error.response.data.errors[0].message);
      } else if (error.response?.data?.non_field_errors) {
        setFormError(error.response.data.non_field_errors[0]);
      } else if (error.response?.data?.detail) {
        setFormError(error.response.data.detail);
      } else {
        setFormError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormError('');
    
    // Validate 2FA code
    if (!twoFactorCode) {
      setFormError('Please enter your authentication code');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const result = await completeTwoFactorAuth(twoFactorCode);
      
      if (result.success) {
        // If 2FA successful, navigate to the redirect URL
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Two-factor authentication failed:', error);
      
      // Handle different error scenarios
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        setFormError(error.response.data.errors[0].message);
      } else {
        setFormError('Two-factor authentication failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If 2FA is required, show the 2FA form
  if (showTwoFactorForm) {
    return (
      <Card className="login-form">
        <h2 className="login-form__title">Two-Factor Authentication</h2>
        <p className="login-form__description">
          Please enter the verification code from your authenticator app.
        </p>
        
        {/* Error message */}
        {(formError || authError) && (
          <div className="login-form__message login-form__message--error">
            {formError || authError}
          </div>
        )}
        
        <form onSubmit={handleTwoFactorSubmit}>
          <div className="login-form__field">
            <label htmlFor="twoFactorCode" className="login-form__label">
              Authentication Code
            </label>
            <input
              id="twoFactorCode"
              type="text"
              className="login-form__input"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder="Enter code"
              disabled={isSubmitting}
              autoComplete="one-time-code"
              required
            />
          </div>
          
          <div className="login-form__footer">
            <div className="login-form__actions">
              <Button 
                type="submit" 
                variant="primary" 
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Verifying...' : 'Verify Code'}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    );
  }
  
  // Regular login form
  return (
    <Card className="login-form">
      <h2 className="login-form__title">Log In to Weaver's World</h2>
      
      {/* Success message */}
      {successMessage && (
        <div className="login-form__message login-form__message--success">
          {successMessage}
        </div>
      )}
      
      {/* Error message */}
      {(formError || authError) && (
        <div className="login-form__message login-form__message--error">
          {formError || authError}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="login-form__field">
          <label htmlFor="email" className="login-form__label">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="login-form__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            disabled={isSubmitting}
            autoComplete="email"
            required
          />
        </div>
        
        <div className="login-form__field">
          <label htmlFor="password" className="login-form__label">
            Password
          </label>
          <input
            id="password"
            type="password"
            className="login-form__input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            disabled={isSubmitting}
            autoComplete="current-password"
            required
          />
        </div>
        
        <div className="login-form__footer">
          <div className="login-form__actions">
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <Link to="/accounts/password/reset/" className="login-form__register-link">
              Forgot Password?
            </Link>
          </div>
          
          <div>
            <span>Don't have an account? </span>
            <Link to="/accounts/signup/" className="login-form__register-link">
              Create Account
            </Link>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default LoginForm;